import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, Factory, Wrench, Network, LineChart, Landmark, ClipboardCheck, Gauge, Award, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useCourse, TIER_COPY, SHOW_READINESS } from "@/lib/hooks";
import { Footer } from "@/components/AppShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const HERO_IMG = "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const useStartTrack = () => {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  return async (track) => {
    if (!user) return navigate("/login", { state: { track } });
    try {
      await api.patch("/auth/track", { track });
      await refreshMe();
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
};

const Hero = ({ user, onPaths }) => (
  <section className="grid-lines border-b border-border">
    <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-10 sm:px-8 md:py-12 lg:grid-cols-12">
      <div className="fade-up lg:col-span-8">
        <div className="overline-label" data-testid="landing-edition-label">Certified Industrial AI Strategist</div>
        <h1 data-testid="landing-title" className="mt-4 font-heading text-4xl font-black leading-[1.05] tracking-tighter sm:text-5xl">
          Learn to scope, test and launch governed AI pilots on the plant floor.
        </h1>
        <p data-testid="landing-objective" className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
          After 3 days you can judge what an AI system can and cannot “see” in your plant data, choose the right approach, and present a deployment-ready pilot brief.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to={user ? "/dashboard" : "/login"} data-testid="landing-start-button" className="group inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3 text-sm font-semibold text-brand-primary-fg transition-transform hover:-translate-y-0.5">
            {user ? "Continue where you left off" : "Sign in to start"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <button type="button" onClick={onPaths} data-testid="landing-paths-button" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors hover:bg-muted">
            Choose your path
          </button>
        </div>
        <div data-testid="landing-facts" className="mt-5 flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs uppercase tracking-wider text-muted-foreground">
          <span>3 days</span><span>·</span><span>8 modules</span><span>·</span><span>no coding required</span><span>·</span><span>3 credentials</span><span>·</span><span>verifiable certificate</span>
        </div>
      </div>
      <div className="relative hidden lg:col-span-4 lg:block">
        <img src={HERO_IMG} alt="Industrial production line interior" className="h-64 w-full rounded-xl object-cover" />
        <div className="absolute inset-0 rounded-xl bg-black/30" />
        <div className="absolute bottom-3 left-3 right-3 rounded-lg border border-white/20 bg-black/55 p-3 text-white backdrop-blur-md">
          <div className="flex items-center gap-2 text-sm font-semibold">
            Machine <span className="pulse-line h-px flex-1 bg-white/70" /> Context <span className="pulse-line h-px flex-1 bg-white/70" /> Decision
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PATHS = [
  { track: "executive", eyebrow: "Executive Briefing", title: "AI Strategy for Industrial Leaders", chips: ["9 short sessions", "~90 minutes", "10-question leader quiz", "Sponsor Ready badge"], cta: "Start the Executive Briefing", icon: Briefcase },
  { track: "strategist", eyebrow: "Full programme", title: "Certified Industrial AI Strategist", chips: ["3 days", "8 modules", "3 credentials", "Pilot brief"], cta: "Start the full programme", icon: Factory },
];

const Paths = () => {
  const start = useStartTrack();
  return (
    <div id="paths" className="grid gap-4 md:grid-cols-2">
      {PATHS.map((p) => (
        <div key={p.track} data-testid={`path-card-${p.track}`} className="flex flex-col rounded-xl border border-border bg-card p-6 transition-transform hover:-translate-y-0.5 sm:p-8">
          <p.icon className="h-6 w-6 text-brand-primary" />
          <div className="mt-3 overline-label">{p.eyebrow}</div>
          <div className="mt-1 font-heading text-2xl font-bold">{p.title}</div>
          <div className="mt-4 flex flex-wrap gap-2">{p.chips.map((c) => <span key={c} className="rounded-full border border-border px-3 py-1 text-xs">{c}</span>)}</div>
          <button type="button" onClick={() => start(p.track)} data-testid={`path-start-${p.track}`} className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-brand-primary-fg hover:opacity-90">
            {p.cta} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

const ROLES = [
  [Factory, "Plant and operations leaders", "Decide where AI is safe and worthwhile to try first."],
  [Wrench, "Process, quality and maintenance engineers", "Turn a production problem into a testable AI use case."],
  [Network, "OT/IT and data leads", "Design the data path so AI answers are current and traceable."],
  [LineChart, "Digital and continuous-improvement managers", "Build the roadmap, the ROI case and the pilot brief."],
  [Landmark, "Executives and board sponsors", "Decide where to invest, what to ask, and how to govern risk.", true],
];

const WhoFor = ({ onPaths }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
    {ROLES.map(([Icon, t, b, exec]) => (
      <div key={t} data-testid="audience-card" className="rounded-xl border border-border bg-card p-5">
        <Icon className="h-5 w-5 text-brand-data" />
        <div className="mt-3 font-heading font-bold leading-snug">{t}</div>
        <p className="mt-2 text-sm text-muted-foreground">{b}</p>
        {exec && <button type="button" onClick={onPaths} data-testid="audience-exec-link" className="mt-3 text-xs font-semibold text-brand-primary">See the Executive Briefing →</button>}
      </div>
    ))}
  </div>
);

const OUTCOMES = [
  "Explain how AI evolved from rules to machine learning to generative models, and choose the right one for each decision.",
  "Spot where a model is “blind” (identity, time, data quality, permissions) and map your plant's blind spots.",
  "Select an architecture and prioritise use cases on a one-line opportunity canvas.",
  "Size cost and ROI, then write a deployment-ready pilot brief.",
  "Earn Foundation → Practitioner → Strategist credentials that others can verify.",
];

const Outcomes = () => (
  <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
    {OUTCOMES.map((o, i) => (
      <li key={o} data-testid="outcome-card" className="rounded-xl border-t-4 border-brand-primary bg-card p-5 shadow-sm">
        <div className="font-heading text-3xl font-black text-brand-primary">{i + 1}</div>
        <p className="mt-2 text-sm">{o}</p>
      </li>
    ))}
  </ol>
);

const ASSESS = [
  ...(SHOW_READINESS ? [[Gauge, "Readiness check", "12 statements, about 3 minutes, private and optional. Shows where your plant is today."]] : []),
  [ListChecks, "Module checks", "10 questions per module, pass with 8 of 10, with an explanation after every answer."],
  [ClipboardCheck, "Line 3 capstone", "A final applied project on a synthetic plant, at Strategist level only."],
  [Award, "Credential ladder", "Foundation → Practitioner → Strategist, each verifiable online."],
];

const Assessed = () => (
  <div className={`grid gap-3 sm:grid-cols-2 ${ASSESS.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
    {ASSESS.map(([Icon, t, b]) => (
      <div key={t} data-testid="assessment-card" className="rounded-xl border border-border bg-card p-5">
        <Icon className="h-5 w-5 text-brand-action" />
        <div className="mt-3 font-heading font-bold">{t}</div>
        <p className="mt-2 text-sm text-muted-foreground">{b}</p>
      </div>
    ))}
  </div>
);

const Ladder = () => (
  <div className="mt-6 grid gap-3 md:grid-cols-3">
    {Object.entries(TIER_COPY).map(([k, t], i) => (
      <div key={k} data-testid={`landing-tier-${k}`} className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-3"><span className="font-heading text-3xl font-black text-brand-primary">{t.mark}</span><span className="font-heading text-lg font-bold">{t.label}</span></div>
        <p className="mt-2 text-sm text-muted-foreground">{t.blurb}</p>
        <p className="mt-3 text-xs text-muted-foreground">{i === 0 ? "Open to every learner" : `Unlocks after ${Object.values(TIER_COPY)[i - 1].label}`}</p>
      </div>
    ))}
  </div>
);

const Syllabus = ({ modules }) => (
  <div>
    <div className="grid gap-4 lg:grid-cols-3">
      {[1, 2, 3].map((day) => (
        <div key={day} className="rounded-xl border border-border bg-card p-5">
          <div className="text-sm font-bold uppercase tracking-widest text-brand-primary">Day {day} · {["See", "Design", "Deploy"][day - 1]}</div>
          <ol className="mt-3 space-y-3">
            {(modules || []).filter((m) => m.day === day).map((m) => (
              <li key={m.id} data-testid={`syllabus-${m.id}`} className="flex gap-3">
                <span className="font-mono text-sm text-brand-data">{m.id}</span>
                <div><div className="text-sm font-semibold leading-snug">{m.title}</div><div className="text-xs text-muted-foreground">{m.lessons.length} lessons · ~{m.minutes} min</div></div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
    <Ladder />
  </div>
);

const TABS = [
  ["paths", "Choose your path"],
  ["audience", "Who it's for"],
  ["outcomes", "What you'll achieve"],
  ["assessment", "How you're assessed"],
  ["syllabus", "Syllabus"],
];

const Closing = ({ edition, user }) => (
  <section className="mx-auto mt-12 max-w-7xl px-4 sm:px-8">
    <div data-testid="landing-closing-cta" className="flex flex-col gap-5 rounded-xl bg-trust p-6 text-white sm:p-8 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <p className="max-w-2xl font-serif text-xl italic leading-snug sm:text-2xl">“Do not ask whether the model is intelligent before asking whether the architecture lets it see the truth.”</p>
        <p className="mt-2 text-sm text-white/60">{edition.doctrine_name} · {edition.brand_name}</p>
      </div>
      <Link to={user ? "/dashboard" : "/login"} data-testid="landing-closing-signin" className="inline-flex w-fit items-center gap-2 rounded-full bg-brand-action px-6 py-3 text-sm font-semibold text-brand-action-fg">{user ? "Go to my path" : "Sign in to start"} <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

export default function Landing() {
  const { edition } = useBrand();
  const { user } = useAuth();
  const { data } = useCourse();
  const [tab, setTab] = useState("paths");
  const tabsRef = useRef(null);
  if (!edition) return null;
  const showPaths = () => { setTab("paths"); tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  return (
    <div data-testid="landing-page">
      <Hero user={user} onPaths={showPaths} />
      <section ref={tabsRef} className="mx-auto max-w-7xl scroll-mt-20 px-4 pt-8 sm:px-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="landing-tabs" className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-full bg-muted p-1 sm:w-auto">
            {TABS.map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`landing-tab-${v}`} className="shrink-0 rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-background data-[state=active]:text-brand-primary data-[state=active]:shadow-sm">{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="paths" className="fade-up mt-6"><Paths /></TabsContent>
          <TabsContent value="audience" className="fade-up mt-6"><WhoFor onPaths={showPaths} /></TabsContent>
          <TabsContent value="outcomes" className="fade-up mt-6"><Outcomes /></TabsContent>
          <TabsContent value="assessment" className="fade-up mt-6"><Assessed /></TabsContent>
          <TabsContent value="syllabus" className="fade-up mt-6"><Syllabus modules={data?.modules} /></TabsContent>
        </Tabs>
      </section>
      <Closing edition={edition} user={user} />
      <Footer />
    </div>
  );
}
